import { SEO } from '../../components/SEO';
import { Link } from 'react-router-dom';

const sections = [
  { id: 'the-service', label: 'The Service' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'free-ai-demo', label: 'Free AI demo' },
  { id: 'bookings', label: 'Bookings and payments' },
  { id: 'acceptable-use', label: 'Acceptable use' },
  { id: 'intellectual-property', label: 'Intellectual property' },
  { id: 'disclaimers', label: 'Disclaimers' },
  { id: 'limitation', label: 'Limitation of liability' },
  { id: 'governing-law', label: 'Governing law' },
  { id: 'contact', label: 'Contact' },
] as const;

export function TermsOfService() {
  return (
    <>
      <SEO
        title="Terms of Service | InterviewR"
        description="Terms and conditions for using InterviewR mock interview platform, AI tools, and expert bookings."
        keywords="InterviewR terms, terms of service, user agreement"
        url={typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com/terms'}
      />
      <main className="min-h-screen bg-stitch-canvas px-4 pb-16 pt-10 sm:px-6 md:pt-14 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-stitch-2xl border border-stitch-border bg-gradient-to-br from-stitch-accent-soft via-stitch-surface to-stitch-primary-soft p-8 shadow-stitch-pop md:p-10">
            <div className="pointer-events-none absolute -left-16 top-0 h-56 w-56 rounded-full bg-stitch-accent/15 blur-3xl" />
            <div className="relative flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-stitch-accent">
                  <span className="material-symbols-outlined text-base">contract</span>
                  Legal
                </p>
                <h1 className="mt-3 text-3xl font-bold tracking-tight text-stitch-fg md:text-4xl">
                  Terms of Service
                </h1>
                <p className="mt-2 text-sm text-stitch-muted">Last updated: April 14, 2026</p>
              </div>
              <div className="flex flex-wrap gap-2 self-start md:self-auto">
                <Link
                  to="/privacy-policy"
                  className="inline-flex items-center justify-center gap-2 rounded-stitch-lg border border-stitch-border bg-stitch-surface px-4 py-2.5 text-sm font-semibold text-stitch-fg shadow-stitch-card transition hover:bg-stitch-surface-2"
                >
                  <span className="material-symbols-outlined text-lg">shield</span>
                  Privacy
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 rounded-stitch-lg border border-stitch-border bg-stitch-surface px-4 py-2.5 text-sm font-semibold text-stitch-fg shadow-stitch-card transition hover:bg-stitch-surface-2"
                >
                  <span className="material-symbols-outlined text-lg">home</span>
                  Home
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-10 lg:grid-cols-12">
            <aside className="lg:col-span-4 xl:col-span-3">
              <nav
                className="sticky top-24 max-h-[calc(100vh-8rem)] space-y-1 overflow-y-auto rounded-stitch-xl border border-stitch-border bg-stitch-surface p-4 shadow-stitch-card"
                aria-label="Terms sections"
              >
                <p className="mb-3 px-2 text-xs font-bold uppercase tracking-wider text-stitch-muted">Contents</p>
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="flex items-center gap-2 rounded-stitch-lg px-3 py-2 text-sm font-medium text-stitch-fg-soft transition hover:bg-stitch-surface-2 hover:text-stitch-fg"
                  >
                    <span className="material-symbols-outlined shrink-0 text-base text-stitch-muted">subdirectory_arrow_right</span>
                    <span className="min-w-0">{s.label}</span>
                  </a>
                ))}
              </nav>
            </aside>

            <article className="lg:col-span-8 xl:col-span-9">
              <div className="rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card md:p-10">
                <p className="text-base leading-relaxed text-stitch-fg-soft">
                  By accessing or using InterviewR at{' '}
                  <a href="https://interview-r.com" className="font-medium text-stitch-primary hover:underline">
                    interview-r.com
                  </a>{' '}
                  (the &quot;Service&quot;), you agree to these Terms. If you do not agree, do not use the Service.
                </p>

                <section id="the-service" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">1. The Service</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    InterviewR connects candidates with independent interviewers and provides AI-assisted practice tools.
                    Interviewers are independent providers; InterviewR is a platform, not an employer of interviewers.
                  </p>
                </section>

                <section id="accounts" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">2. Accounts</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    You must provide accurate information and keep credentials secure. You are responsible for activity
                    under your account. We may suspend accounts that violate these Terms or harm other users.
                  </p>
                </section>

                <section id="free-ai-demo" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">3. Free AI demo</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    Anonymous AI practice may be limited (e.g. messages per session, sessions per day per network). Full
                    features require registration. AI output is for practice only and may be inaccurate; do not rely on
                    it as legal or professional advice.
                  </p>
                </section>

                <section id="bookings" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">4. Bookings and payments</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    Paid sessions are subject to the cancellation and refund rules shown at checkout and on booking
                    screens. Payment processing is handled by third-party providers; their terms may also apply.
                  </p>
                </section>

                <section id="acceptable-use" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">5. Acceptable use</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">You agree not to:</p>
                  <ul className="mt-3 list-disc space-y-2 pl-6 text-stitch-fg-soft">
                    <li>Abuse, harass, or defraud other users or staff.</li>
                    <li>Attempt to scrape, overload, or compromise the Service or AI providers.</li>
                    <li>Use the Service for unlawful purposes or to transmit malware.</li>
                    <li>Circumvent rate limits, access controls, or payment requirements.</li>
                  </ul>
                </section>

                <section id="intellectual-property" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">6. Intellectual property</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    The Service, branding, and software are owned by InterviewR or its licensors. You retain rights to
                    content you submit; you grant us a license to host and display it as needed to operate the Service.
                  </p>
                </section>

                <section id="disclaimers" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">7. Disclaimers</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    The Service is provided &quot;as is&quot;. We do not guarantee job offers, interview outcomes, or
                    uninterrupted availability. To the fullest extent permitted by law, we disclaim implied warranties.
                  </p>
                </section>

                <section id="limitation" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">8. Limitation of liability</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    To the fullest extent permitted by law, InterviewR and its operator shall not be liable for indirect,
                    incidental, or consequential damages, or for loss of profits or data. Our total liability for claims
                    relating to the Service shall not exceed the amounts you paid to us in the twelve months before the
                    claim, or fifty USD if greater where required by law.
                  </p>
                </section>

                <section id="governing-law" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">9. Governing law</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    These Terms are governed by the laws of Georgia, without regard to conflict-of-law rules, unless
                    mandatory consumer protections in your country say otherwise.
                  </p>
                </section>

                <section id="contact" className="mt-10 scroll-mt-28 border-t border-stitch-border pt-10">
                  <h2 className="text-xl font-bold text-stitch-fg">10. Contact</h2>
                  <p className="mt-4 leading-relaxed text-stitch-fg-soft">
                    Questions:{' '}
                    <a href="mailto:info@interview-r.com" className="font-medium text-stitch-primary hover:underline">
                      info@interview-r.com
                    </a>
                  </p>
                </section>

                <div className="mt-12 flex flex-wrap gap-3 border-t border-stitch-border pt-8">
                  <Link
                    to="/privacy-policy"
                    className="inline-flex items-center gap-2 rounded-stitch-lg bg-stitch-primary px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-stitch-primary/25 hover:bg-stitch-primary-hover"
                  >
                    Privacy Policy
                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </div>
      </main>
    </>
  );
}
